import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Calendar, CheckCircle2, Loader2, ShieldCheck, Sparkles, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import {
  assignInitialObPlan,
  CompensationServiceError,
  type ObAssignInitialAuditFailure
} from '@/services/compensationService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ObInitialAssignDialog', 'compensation');

interface SelectableSchedule {
  schedule_id: number;
  schedule_name: string;
  compensation_percentage: number | null;
  compensation_max_cap: number | null;
}

interface ObInitialAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  targetUserName?: string;
  onAssigned?: () => void;
}

function formatMoney(n: number | null | undefined): string {
  if (n == null || n === 0) {
return 'No cap';
}
  return `$${n.toLocaleString()}`;
}

function formatPct(n: number | null | undefined): string {
  return n == null ? '—' : `${n}%`;
}

// Today in LA — the server derives the actual OB write timestamp, but the admin
// still needs a clear read-only view of the intended calendar date.
function todayLaDisplay(): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  return fmt.format(new Date());
}

export function ObInitialAssignDialog({
  open,
  onOpenChange,
  targetUserId,
  targetUserName,
  onAssigned
}: ObInitialAssignDialogProps): JSX.Element {
  const [schedules, setSchedules] = useState<SelectableSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [auditFailures, setAuditFailures] = useState<ObAssignInitialAuditFailure[] | null>(null);

  useEffect(() => {
    if (!open) {
return;
}
    setSelectedScheduleId(null);
    setAuditFailures(null);
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('ob_comp_schedules')
        .select('schedule_id, schedule_name, compensation_percentage, compensation_max_cap')
        .eq('is_active', true)
        .eq('is_selectable_for_lo_self_service', true)
        .order('schedule_name');
      if (error) {
        log.error('Failed to load selectable schedules', {}, error);
        toast.error('Failed to load selectable plans');
        setLoading(false);
        return;
      }
      setSchedules((data ?? []) as SelectableSchedule[]);
      setLoading(false);
    })();
  }, [open]);

  const selected = useMemo(
    () => schedules.find((s) => s.schedule_id === selectedScheduleId) ?? null,
    [schedules, selectedScheduleId]
  );

  const effectiveDisplay = useMemo(() => todayLaDisplay(), []);

  const handleSubmit = async () => {
    if (!selectedScheduleId) {
      toast.error('Pick a plan');
      return;
    }
    setSubmitting(true);
    setAuditFailures(null);
    try {
      const result = await assignInitialObPlan(targetUserId, selectedScheduleId);
      if (result.status === 'audit_failed') {
        // OB writes reached OB but the read-back audit found mismatches per-category.
        // Surface the structured failures inline; do NOT close the dialog — the admin
        // may need to retry or investigate. The local cache was NOT updated.
        setAuditFailures(result.failures ?? []);
        toast.error(
          `Audit failed: ${result.failures?.length ?? 0} categor${(result.failures?.length ?? 0) === 1 ? 'y' : 'ies'} did not match`
        );
        log.warn('assignInitialObPlan audit_failed', { targetUserId, selectedScheduleId, failures: result.failures });
        return;
      }
      toast.success(`${result.scheduleName ?? 'Plan'} assigned · effective ${result.effectiveDate ?? effectiveDisplay}`);
      onAssigned?.();
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Assignment failed: ${msg}`);
      log.error('assignInitialObPlan failed', { targetUserId, selectedScheduleId }, e as Error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[90dvh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 bg-gradient-to-br from-slate-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <UserPlus className="h-5 w-5 text-blue-600" />
            Set Up Compensation Plan
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 mt-1">
            <Calendar className="h-3.5 w-3.5" />
            Effective <span className="font-medium text-foreground">{effectiveDisplay}</span>
            {' '}· admin-only first-time setup
            {targetUserName ? <> for <span className="font-medium text-foreground">{targetUserName}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-slate-50/50">
          <Alert className="bg-blue-50 border-blue-200">
            <ShieldCheck className="h-4 w-4 text-blue-700" />
            <AlertTitle className="text-blue-900">First-time setup</AlertTitle>
            <AlertDescription className="text-blue-900">
              Assigns the selected plan across the 14 standard core product categories in Optimal Blue
              and audits the write. Lendwise only updates after live OB confirms the plan is active now.
              This is an admin-only setup action, not the self-serve Change Plan flow.
            </AlertDescription>
          </Alert>

          {auditFailures && auditFailures.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Audit failed — Lendwise cache NOT updated</AlertTitle>
              <AlertDescription>
                <p className="mb-2">
                  {auditFailures.length} categor{auditFailures.length === 1 ? 'y' : 'ies'} did not match the intended schedule after the write.
                  OB may have rejected part of the batch. Retry or investigate directly in OB.
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {auditFailures.map((f) => (
                    <li key={f.productCategory}>
                      <span className="font-mono">{f.productCategory}</span>
                      {' — expected schedule '}
                      <span className="font-medium">{f.expectedScheduleId}</span>
                      {', got '}
                      <span className="font-medium">
                        {f.actualScheduleId == null ? 'nothing' : f.actualScheduleId}
                      </span>
                      {f.actualEffectiveDate ? ` (effective ${f.actualEffectiveDate.slice(0, 10)})` : ''}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Plan preview — empty until selected */}
          <div
            className={
              selected
                ? 'rounded-lg border-2 border-blue-500 bg-blue-50/50 p-4 shadow-sm transition-all'
                : 'rounded-lg border-2 border-dashed border-muted-foreground/20 bg-white/40 p-4 transition-all'
            }
          >
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mb-2">
              {selected ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-blue-700">Selected</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  Pick a plan below
                </>
              )}
            </div>
            {selected ? (
              <>
                <div className="font-semibold text-base mb-2">{selected.schedule_name}</div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <span>{formatPct(selected.compensation_percentage)}</span>
                  <span>·</span>
                  <span>cap {formatMoney(selected.compensation_max_cap)}</span>
                  <span>·</span>
                  <span>14 core categories</span>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground italic mt-1">No plan selected yet</div>
            )}
          </div>

          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <Label htmlFor="initial-plan-select" className="text-sm font-semibold mb-2 block">
              Available plans
            </Label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading plans…
              </div>
            ) : schedules.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No plans are currently selectable. Enable plans in the OB Plans tab first.
              </p>
            ) : (
              <>
                <Select
                  value={selectedScheduleId != null ? String(selectedScheduleId) : ''}
                  onValueChange={(v) => setSelectedScheduleId(Number(v))}
                >
                  <SelectTrigger id="initial-plan-select" className="w-full">
                    <SelectValue placeholder="Choose a plan…" />
                  </SelectTrigger>
                  <SelectContent>
                    {schedules.map((s) => (
                      <SelectItem key={s.schedule_id} value={String(s.schedule_id)}>
                        <span className="font-medium">{s.schedule_name}</span>
                        <span className="ml-2 text-muted-foreground text-sm">
                          · {formatPct(s.compensation_percentage)} · cap {formatMoney(s.compensation_max_cap)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  {schedules.length} {schedules.length === 1 ? 'plan' : 'plans'} available
                </p>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedScheduleId || submitting || schedules.length === 0}
            className="w-full sm:w-auto"
          >
            {submitting ? 'Assigning…' : 'Assign Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
